import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { translate } from "../../translate/translate";
import { RFCExecution } from "../../models/general/import";

export const RFC_TYPE_LABEL: Record<string, string> = {
  BOM: translate.t("bom"),
  FORECAST: translate.t("forecast"),
  STOCK_PO: translate.t("stock_and_po"),
};

export const useRFCDialog = (rfcsap: RFCExecution | null | undefined) => {
  const [showMessage, setShowMessage] = useState(false);
  const [showAllMaterials, setShowAllMaterials] = useState(false);

  const isBOM = rfcsap?.rfc_type === "BOM";

  const shouldShowDetails =
    rfcsap?.status === "ERROR" ||
    rfcsap?.status === "PARTIALLY_UPDATED";

  const getStatusLabel = (status?: string) => {
    const map: Record<string, string> = {
      PROCESSING: translate.t("processing_import"),
      UPDATED: translate.t("updated"),
      PARTIALLY_UPDATED: translate.t("partially_updated"),
      ERROR: translate.t("error"),
    };
    return map[status || ""] || status || "---";
  };

  const getStatusColor = (status?: string) => {
    const map: Record<string, string> = {
      UPDATED: "#4CAF4F",
      PARTIALLY_UPDATED: "#FF9800",
      ERROR: "#F44336",
      PROCESSING: "#3639f4ff",
    };
    return map[status || ""] || "#212121";
  };

  const formatDateTime = (iso?: string) => {
    if (!iso) return "---";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()} - ${String(
      d.getHours()
    ).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}min`;
  };

  const formatDuration = (seconds?: number | null) => {
    if (!seconds) return "---";
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return hours > 0 ? `${hours}h${minutes % 60}min` : `${minutes}min`;
  };

  const extractFailed = (errorMessage?: string): string[] => {
    if (!errorMessage) return [];
    try {
      const parsed = JSON.parse(errorMessage);
      return typeof parsed === "object" && !Array.isArray(parsed)
        ? Object.keys(parsed)
        : [];
    } catch {
      return [];
    }
  };

  const failedBOMs = useMemo(
    () => extractFailed(rfcsap?.error_message),
    [rfcsap]
  );

  const errorMessageDisplay = useMemo(() => {
    if (!rfcsap?.error_message) return "---";
    return rfcsap.error_message;
  }, [rfcsap]);

  const getMaterialsPreview = (
    materials: string[],
    limit = 6
  ): string => {
    return materials.length <= limit
      ? materials.join(", ")
      : materials.slice(0, limit).join(", ");
  };

  const copyToClipboard = async (text: string) => {
    if (text === "---") return false;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn("Clipboard API falhou, usando fallback:", err);
      }
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.opacity = "0";

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      return document.execCommand("copy");
    } catch (err) {
      console.error("Fallback copy falhou:", err);
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(errorMessageDisplay);

    if (success) {
      setShowMessage(true);
      setTimeout(() => setShowMessage(false), 2000);
    } else {
      toast.error(translate.t("copy_failed"));
    }
  };

  return {
    showMessage,
    showAllMaterials,
    setShowAllMaterials,
    isBOM,
    shouldShowDetails,
    failedBOMs,
    errorMessageDisplay,
    getStatusLabel,
    getStatusColor,
    formatDateTime,
    formatDuration,
    getMaterialsPreview,
    handleCopy,
  };
};
